/**
 * ConnectorTaskInspector — Phase 6a-2.
 *
 * Edits a `connector_task` node — the workflow's bridge to installed
 * connectors (Slack, Jira, ServiceNow, HTTP-glue, etc.). This is the
 * second-most-used node type after http_task in real customer workflows
 * (per the 79-type palette audit), so a clean Inspector materially
 * unlocks V3 for non-trivial flows.
 *
 * Config shape (per soarNodes.js:809 + matched against V3 lines 11868-
 * 11975 of CanvasPageV3.jsx so persisted V3 workflows round-trip):
 *
 *   connectorId    UUID            installed-connector primary key
 *   action         string          chosen skill / operation id
 *   parameters     object          per-skill parameter payload
 *   skill          string (legacy) older marketplace-templated nodes
 *                                  wrote the chosen op here instead of
 *                                  `action`. We READ both; we WRITE
 *                                  `action` going forward.
 *
 * Data sources:
 *   - PlatformContext.connectors  — installed connector rows; already
 *                                    loaded by app shell, no extra fetch.
 *   - api.getConnectorSkills(id)  — per-connector skill catalogue, lazy
 *                                    fetched on connector selection and
 *                                    cached in component state for the
 *                                    lifetime of the Inspector mount.
 *
 * Spread-preservation contract per useCanvas:1098 — every onUpdate
 * patch spreads the existing `config` so V3-only fields (outputMapping,
 * retryPolicy, connectorType, skillParams, etc.) survive a V3 save.
 * Parameter writes additionally spread `cfg.parameters` so individual
 * field edits don't blow away sibling parameters.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { usePlatform } from '../../../contexts/PlatformContext';
import { getConnectorSkills, getConnector, getConnectorManifest } from '../../../services/api';

// Connectors may arrive flat ({id,name,type}) or wrapped ({connector:{...}}) depending on the
// source endpoint. Read defensively so a bound connector is never dropped by a shape mismatch.
function connField(c, key) {
  if (!c) return undefined;
  if (c[key] != null) return c[key];
  if (c.connector && c.connector[key] != null) return c.connector[key];
  return undefined;
}

export default function ConnectorTaskInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const platform = useSafePlatform();
  const installedConnectors = platform?.connectors || [];

  // V3 wrote either `action` or `skill` depending on era — read both.
  const selectedConnectorId = cfg.connectorId || '';
  const selectedActionId    = cfg.action || cfg.skill || '';
  const parameters          = (cfg.parameters && typeof cfg.parameters === 'object') ? cfg.parameters : {};

  // Per-connector skill catalogue — lazy fetched on first selection.
  // Cached in state so toggling between connectors in the same Inspector
  // session doesn't refetch repeatedly. Keyed by connector id.
  const [skillsByConnector, setSkillsByConnector] = useState({});
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [skillsError, setSkillsError] = useState(null);

  // Hydrate the bound connector by id when it isn't in the app-shell's installed-connector list.
  // The installed list is scoped to the ACTIVE org/tenant; a workflow authored in another scope
  // (e.g. a System risk workflow bound to a tenant-specific Salesforce connector) would otherwise
  // render its bound connector as "(none)" and lock the operator out of editing the node. We fetch
  // it once by id (the single-connector endpoint is not scope-filtered), unwrap, and keep in state.
  // Parity with the v4 inspector (#218); this is the component the live /canvas actually mounts.
  const [fetchedConnector, setFetchedConnector] = useState(null);
  const boundInList = useMemo(
    () => installedConnectors.some((c) => connField(c, 'id') === selectedConnectorId),
    [installedConnectors, selectedConnectorId]
  );
  useEffect(() => {
    if (!selectedConnectorId || boundInList) { setFetchedConnector(null); return; }
    let cancelled = false;
    getConnector(selectedConnectorId)
      .then((res) => {
        if (cancelled) return;
        // api.getConnector unwraps the sharing envelope; read fields directly.
        const conn = (res && 'data' in res) ? res.data : res;
        if (conn && (conn.id || selectedConnectorId)) {
          setFetchedConnector({ id: conn.id || selectedConnectorId, name: conn.name, type: conn.type, status: conn.status });
        }
      })
      .catch((err) => { if (!cancelled) console.warn('[ConnectorTaskInspector] getConnector(bound) failed', err); });
    return () => { cancelled = true; };
  }, [selectedConnectorId, boundInList]);

  // The connector object backing the current selection (list first, else the by-id hydration above).
  // Drives the manifest lookup (needs the type) when the connector has no linked skills.
  const activeConnector = useMemo(() => {
    const inList = installedConnectors.find((c) => connField(c, 'id') === selectedConnectorId);
    if (inList) return { id: connField(inList, 'id'), name: connField(inList, 'name'), type: connField(inList, 'type'), status: connField(inList, 'status') };
    return fetchedConnector || null;
  }, [installedConnectors, selectedConnectorId, fetchedConnector]);
  const activeConnectorType = activeConnector?.type || '';

  useEffect(() => {
    if (!selectedConnectorId) return;
    if (skillsByConnector[selectedConnectorId]) return; // cached
    let cancelled = false;
    setSkillsLoading(true);
    setSkillsError(null);
    // Actions come from the connector's LINKED skills when present, else fall back to the registry
    // MANIFEST (the same single source of truth the workflow engine + MCP gateway use to resolve
    // action → endpoint/method) — so a manifest-backed connector (e.g. Salesforce get_record) shows
    // its real actions instead of "exposes no actions yet".
    const parseList = (res) => {
      const payload = (res && 'data' in res) ? res.data : res;
      return Array.isArray(payload) ? payload : (Array.isArray(payload?.skills) ? payload.skills : []);
    };
    getConnectorSkills(selectedConnectorId)
      .then(async (res) => {
        if (cancelled) return;
        let list = parseList(res);
        if ((!list || list.length === 0) && activeConnectorType) {
          try {
            const m = await getConnectorManifest(activeConnectorType);
            if (!cancelled) {
              const mp = (m && 'data' in m) ? m.data : m;
              list = Array.isArray(mp?.skills) && mp.skills.length ? mp.skills
                   : (Array.isArray(mp?.operations) ? mp.operations : []);
            }
          } catch (mErr) {
            console.warn('[ConnectorTaskInspector] getConnectorManifest fallback failed', mErr);
          }
        }
        if (!cancelled) setSkillsByConnector((prev) => ({ ...prev, [selectedConnectorId]: list || [] }));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[ConnectorTaskInspector] getConnectorSkills failed', err);
        setSkillsError(err?.message || String(err));
        setSkillsByConnector((prev) => ({ ...prev, [selectedConnectorId]: [] }));
      })
      .finally(() => { if (!cancelled) setSkillsLoading(false); });
    return () => { cancelled = true; };
  }, [selectedConnectorId, activeConnectorType]); // eslint-disable-line

  // Resolve labels for the picker. Installed connectors first (sorted by
  // name), then a single "Marketplace skills loaded on selection" hint.
  //
  // RC-ConnectorTaskInspector-localeCompare — defensive sort key. The
  // pre-filter already requires c.id so `label` falls back to it; we add
  // the `|| ''` on the sort key anyway as drift protection (if a future
  // BE shape change introduces null/undefined into the cascade, the
  // panel keeps rendering instead of crashing the whole Task panel).
  const connectorOptions = useMemo(() => {
    const opts = (installedConnectors || [])
      .filter((c) => connField(c, 'id'))
      .map((c) => ({
        value: connField(c, 'id'),
        label: connField(c, 'name') || connField(c, 'type') || connField(c, 'id'),
        type:  connField(c, 'type') || '',
        status: connField(c, 'status') || '',
      }));
    // The bound connector hydrated by id when it isn't in the active-scope installed list — so a
    // node bound to an out-of-scope connector renders (and stays editable) instead of "(none)".
    if (fetchedConnector && !opts.some((o) => o.value === fetchedConnector.id)) {
      opts.push({
        value: fetchedConnector.id,
        label: fetchedConnector.name || fetchedConnector.type || fetchedConnector.id,
        type: fetchedConnector.type || '',
        status: fetchedConnector.status || '',
      });
    }
    return opts.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [installedConnectors, fetchedConnector]);

  const activeSkills = selectedConnectorId ? (skillsByConnector[selectedConnectorId] || []) : [];

  // RC-ConnectorTaskInspector-localeCompare — when the BE returns a skill
  // row with NO method, NO name, AND NO id (sparse / partially-imported
  // skill), the label cascade `s.name || s.id` evaluates to undefined.
  // The downstream `.sort((a,b) => a.label.localeCompare(b.label))` then
  // crashes the entire Task panel with "Cannot read properties of
  // undefined (reading 'localeCompare')". The crash is intermittent
  // because it depends on which connector is selected — some
  // connectors emit complete skill rows, others don't.
  //
  // Fix (two layers, both load-bearing):
  //   1. Drop unrenderable rows BEFORE the map — same pattern
  //      connectorOptions uses on c.id. A skill we can't label is one
  //      the operator can't select anyway.
  //   2. Null-safe sort key as drift protection in case a future BE
  //      shape regression sneaks an undefined past the filter.
  const actionOptions = useMemo(() => {
    if (!activeSkills.length) return [];
    return activeSkills
      .filter((s) => s && (s.name || s.id))
      .map((s) => ({
        value: s.id || s.name,
        label: s.method ? `[${s.method}] ${s.name || s.id}` : (s.name || s.id),
        skill: s,
      }))
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }, [activeSkills]);

  const selectedSkill = useMemo(() => {
    return actionOptions.find((a) => a.value === selectedActionId)?.skill || null;
  }, [actionOptions, selectedActionId]);

  // ── mutation helpers ────────────────────────────────────────────────
  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  // Parameter writes — spread `parameters` so per-key edits don't drop
  // sibling fields. Undefined value means "delete this key".
  const setParam = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    const next = { ...parameters };
    if (value === undefined) delete next[key];
    else next[key] = value;
    onUpdate(node.id, { config: { ...cfg, parameters: next } });
  };

  // Picking a new connector wipes the now-stale action + parameters.
  // (V3 leaves them — but that produces silent run-time errors when the
  // user picks a connector that doesn't have the previously-chosen op.)
  const pickConnector = (id) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    if (!id || id === selectedConnectorId) { setField('connectorId', id); return; }
    onUpdate(node.id, {
      config: { ...cfg, connectorId: id, action: '', skill: '', parameters: {} },
    });
  };

  // Picking a new action keeps the parameters object (the user may have
  // partial data they want to preserve) but writes to `action` (not
  // `skill`) so we converge persisted data on the canonical field name.
  const pickAction = (id) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, {
      config: { ...cfg, action: id, skill: undefined },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Connector"
        hint={connectorOptions.length === 0
          ? 'No installed connectors visible. Install one on the Connectors page first.'
          : 'Choose an installed connector for this tenant.'}
      >
        <select
          value={selectedConnectorId}
          onChange={(e) => pickConnector(e.target.value)}
          disabled={readOnly || connectorOptions.length === 0}
          style={selectStyle}
        >
          <option value="">(none)</option>
          {connectorOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}{c.status && c.status !== 'ACTIVE' ? ` — ${c.status}` : ''}
            </option>
          ))}
        </select>
      </FieldGroup>

      <FieldGroup
        label="Action"
        hint={!selectedConnectorId
          ? 'Pick a connector first.'
          : skillsLoading
            ? 'Loading actions…'
            : skillsError
              ? `Couldn't load actions: ${skillsError}`
              : (actionOptions.length === 0
                ? 'This connector exposes no actions yet — link a skill on the Connectors page.'
                : 'Choose the operation to invoke.')}
      >
        <select
          value={selectedActionId}
          onChange={(e) => pickAction(e.target.value)}
          disabled={readOnly || !selectedConnectorId || skillsLoading || actionOptions.length === 0}
          style={selectStyle}
        >
          <option value="">(none)</option>
          {actionOptions.map((a) => (
            <option key={a.value} value={a.value}>{a.label}</option>
          ))}
          {/* If the persisted action isn't in the freshly fetched list
              (renamed upstream, or stale config), surface it as a custom
              option so we don't silently drop the user's saved value. */}
          {selectedActionId && !actionOptions.find((a) => a.value === selectedActionId) && (
            <option value={selectedActionId}>{selectedActionId} (legacy)</option>
          )}
        </select>
      </FieldGroup>

      {/* Per-skill parameter form. If the BE returns a parameter schema
          for this skill we render a typed form; otherwise we fall back
          to a free-form JSON textarea so the operator can still author
          a payload. Both write to the same cfg.parameters object. */}
      {selectedSkill && Array.isArray(selectedSkill.parameters) && selectedSkill.parameters.length > 0 ? (
        <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{
            fontSize: 10, fontWeight: 700, color: '#475569',
            textTransform: 'uppercase', letterSpacing: 0.5,
            paddingBottom: 4, borderBottom: '0.5px solid #e2e8f0',
          }}>
            Parameters
          </div>
          {selectedSkill.parameters.map((p) => (
            <SkillParamField
              key={p.name || p.field || p.key}
              param={p}
              value={parameters[p.name || p.field || p.key]}
              onChange={(v) => setParam(p.name || p.field || p.key, v)}
              readOnly={readOnly}
            />
          ))}
        </section>
      ) : (
        <FieldGroup
          label="Parameters (raw JSON)"
          hint={selectedSkill
            ? 'Action has no published parameter schema — author parameters as JSON.'
            : 'Pick an action above to render its typed fields.'}
        >
          <textarea
            value={stringifyJson(parameters)}
            onChange={(e) => {
              const parsed = parseJsonInput(e.target.value);
              setField('parameters', parsed === undefined ? {} : parsed);
            }}
            placeholder='{ "channel": "#alerts", "text": "{{message}}" }'
            disabled={readOnly}
            rows={6}
            spellCheck={false}
            style={{
              ...inputStyle,
              fontFamily: 'SFMono-Regular, Menlo, monospace',
              fontSize: 11,
              lineHeight: 1.4,
              resize: 'vertical',
            }}
          />
        </FieldGroup>
      )}
    </div>
  );
}

/* ───── single-parameter renderer ─────────────────────────────────────
 * Mirrors the small subset of `type` values the BE skill schema uses
 * today (string/number/boolean/select/json). Anything unknown falls
 * back to a string input so the operator isn't blocked.
 * ─────────────────────────────────────────────────────────────────── */
function SkillParamField({ param, value, onChange, readOnly }) {
  const label = param.label || param.name || param.field || 'Parameter';
  const hint = param.helpText || param.description || (param.required ? 'Required.' : undefined);
  const type = (param.type || 'string').toLowerCase();

  if (type === 'boolean') {
    return (
      <FieldGroup label={label} hint={hint}>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#0f172a' }}>
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            disabled={readOnly}
          />
          {value === true ? 'true' : 'false'}
        </label>
      </FieldGroup>
    );
  }

  if (type === 'number' || type === 'integer') {
    return (
      <FieldGroup label={label} hint={hint}>
        <input
          type="number"
          value={value === undefined || value === null ? '' : value}
          onChange={(e) => {
            const v = e.target.value;
            if (v === '') return onChange(undefined);
            const n = Number(v);
            if (Number.isFinite(n)) onChange(n);
          }}
          placeholder={param.placeholder || ''}
          disabled={readOnly}
          style={inputStyle}
        />
      </FieldGroup>
    );
  }

  if (type === 'select' || (Array.isArray(param.options) && param.options.length > 0)) {
    const opts = (param.options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
    return (
      <FieldGroup label={label} hint={hint}>
        <select
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          <option value="">(none)</option>
          {opts.map((o) => (
            <option key={String(o.value)} value={o.value}>{o.label}</option>
          ))}
        </select>
      </FieldGroup>
    );
  }

  if (type === 'json' || type === 'object' || type === 'array' || type === 'textarea') {
    return (
      <FieldGroup label={label} hint={hint}>
        <textarea
          value={stringifyJson(value)}
          onChange={(e) => {
            const parsed = parseJsonInput(e.target.value);
            onChange(parsed);
          }}
          placeholder={param.placeholder || ''}
          disabled={readOnly}
          rows={3}
          spellCheck={false}
          style={{
            ...inputStyle,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            resize: 'vertical',
          }}
        />
      </FieldGroup>
    );
  }

  // default: string
  return (
    <FieldGroup label={label} hint={hint}>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        placeholder={param.placeholder || ''}
        disabled={readOnly}
        autoComplete="off"
        style={inputStyle}
      />
    </FieldGroup>
  );
}

/* ───── helpers ───────────────────────────────────────────────────── */

function useSafePlatform() {
  // PlatformContext throws if used outside its provider. The V4 canvas
  // always mounts inside the provider in production, but defensive
  // fallback keeps Storybook / unit-test mounts of this Inspector from
  // crashing the whole canvas surface.
  try { return usePlatform(); } catch (_) { return null; }
}

function stringifyJson(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch (_) { return String(v); }
}

function parseJsonInput(raw) {
  if (typeof raw !== 'string') return raw;
  if (raw.trim() === '') return undefined;
  try { return JSON.parse(raw); } catch (_) { return raw; }
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'auto',
};
